<script lang="ts">
/** Прокрутка рейла между страницами: каждая страница монтирует свой MainLayout, и рейл пересоздаётся. */
let savedScrollTop: number | null = null;
</script>

<script setup lang="ts">
/**
 * Рейл — первый уровень навигации: иконки разделов на всю высоту экрана.
 * Лист ведёт на страницу сразу, группа открывает панель подразделов (см. AppSidebarPanel).
 */
import { usePreferredReducedMotion } from '@vueuse/core';
import { computed, onBeforeUnmount, onMounted, useTemplateRef, watch } from 'vue';
import { RouterLink } from 'vue-router';

import { UserMenu } from '@/features/session';
import { useScrollFade } from '@/shared/lib/composables';
import { BaseTooltip } from '@/shared/ui/tooltip';

import type { MenuItem } from '../model/menu-types';

import { isMenuGroup } from '../model/menu-types';
import { useMenuBadges } from '../model/use-menu-badges';
import { useMenuNavigation } from '../model/use-menu-navigation';
import { useUserMenuItems } from '../model/use-user-menu-items';
import LocationPlaque from './LocationPlaque.vue';
import SidebarRailIcon from './SidebarRailIcon.vue';

interface Props {
  /** Уже отфильтрованные по правам пункты меню */
  items: MenuItem[];
  /** Группа текущей страницы — её иконка окрашена акцентом */
  activeGroupId: string | null;
  /** Группа, чья панель сейчас открыта (закреплённая или preview) */
  openGroupId: string | null;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'group-click': [id: string];
}>();

const { isLinkActive, isLinkHighlighted, handleLinkClick } = useMenuNavigation();
const { badgeCount, badgeLabel, formatBadgeCount } = useMenuBadges(() => props.items);
const userMenuItems = useUserMenuItems();

const menu = useTemplateRef<HTMLElement>('menu');
const preferredMotion = usePreferredReducedMotion();
/** Край со скрытыми иконками затухает, как в AppSegmentedTabs. */
const { maskClass, updateFade, reveal } = useScrollFade(menu, { axis: 'y' });

/** Открытая панелью группа, иначе раздел текущей страницы. */
const selectedId = computed(
  () =>
    props.openGroupId ??
    props.activeGroupId ??
    props.items.find((item) => !isMenuGroup(item) && isLinkHighlighted(item))?.id ??
    null,
);

/** Докручивает рейл до выбранной иконки, не трогая прокрутку страницы. */
function revealSelected(smooth: boolean): void {
  const item = selectedId.value ? menu.value?.querySelector<HTMLElement>(`[data-rail-id="${selectedId.value}"]`) : null;
  reveal(item, smooth && preferredMotion.value !== 'reduce' ? 'smooth' : 'instant');
}

// После перехода рейл продолжает с прежней позиции и докручивается плавно;
// при загрузке страницы и смене набора пунктов без анимации.
onMounted(() => {
  if (menu.value && savedScrollTop !== null) menu.value.scrollTop = savedScrollTop;
  revealSelected(savedScrollTop !== null);
});
onBeforeUnmount(() => {
  savedScrollTop = menu.value?.scrollTop ?? null;
});
watch(selectedId, () => revealSelected(true), { flush: 'post' });
watch(
  () => props.items.length,
  () => revealSelected(false),
  { flush: 'post' },
);
</script>

<template>
  <div class="rail">
    <div class="rail__logo">
      <LocationPlaque variant="rail" />
    </div>

    <nav
      ref="menu"
      class="rail__menu"
      :class="maskClass"
      aria-label="Разделы"
      @scroll.passive="updateFade"
    >
      <template
        v-for="item in items"
        :key="item.id"
      >
        <BaseTooltip
          :content="item.label"
          placement="right"
        >
          <RouterLink
            v-if="!isMenuGroup(item)"
            v-slot="{ href, navigate }"
            :to="item.to"
            custom
          >
            <a
              :href="href"
              class="rail__item"
              :data-rail-id="item.id"
              :aria-label="item.label"
              :aria-current="isLinkActive(item) ? 'page' : isLinkHighlighted(item) ? 'location' : undefined"
              @click="handleLinkClick(item, navigate, $event)"
            >
              <span
                class="rail__box"
                :class="{ 'rail__box--active': isLinkHighlighted(item) }"
              >
                <SidebarRailIcon
                  :icon="item.icon"
                  :count="badgeCount(item)"
                  :label="badgeLabel(item)"
                  :text="formatBadgeCount(badgeCount(item))"
                />
              </span>
            </a>
          </RouterLink>

          <button
            v-else
            type="button"
            class="rail__item"
            :data-rail-id="item.id"
            :data-group-id="item.id"
            :aria-label="item.label"
            :aria-controls="`app-sidebar-panel-${item.id}`"
            :aria-expanded="openGroupId === item.id"
            @click="emit('group-click', item.id)"
          >
            <span
              class="rail__box"
              :class="{ 'rail__box--active': activeGroupId === item.id }"
            >
              <SidebarRailIcon
                :icon="item.icon"
                :count="badgeCount(item)"
                :label="badgeLabel(item)"
                :text="formatBadgeCount(badgeCount(item))"
              />
            </span>
          </button>
        </BaseTooltip>
      </template>
    </nav>

    <div class="rail__footer">
      <UserMenu
        :collapsed="true"
        :extra-items="userMenuItems"
      />
    </div>
  </div>
</template>

<style scoped>
.rail {
  /* Выше preview-панели: она выезжает из-под рейла, а не поверх него. */
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: var(--sidebar-rail-width);
  height: 100%;
  background: var(--base-container-bg);
  border-right: 1px solid var(--base-border-color);
}

.rail__logo {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: var(--sidebar-rail-width);
  height: var(--sidebar-rail-width);
  color: var(--sidebar-logo-color);
}

.rail__menu {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: none;
}

.rail__menu::-webkit-scrollbar {
  width: 0;
}

.rail__item {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: var(--sidebar-rail-width);
  height: 52px;
  padding: 0;
  background: transparent;
  border: none;
  color: var(--base-secondary-text-color);
  cursor: pointer;
  font: inherit;
}

.rail__box {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 8px;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;
}

/* Линии иконок 1.5px независимо от viewBox: наши SVG нарисованы со stroke 2, что при
   24px на тёмном фоне тяжелее эталона; non-scaling-stroke считает ширину в экранных px. */
.rail__box :deep(svg *) {
  vector-effect: non-scaling-stroke;
  stroke-width: 1.5px;
}

.rail__item:hover .rail__box {
  background: var(--base-main-bg);
}

/* Выбранный раздел — цвет иконки и полоска у левого края: залитый квадрат сливался с плашкой локации. */
.rail__box--active {
  color: var(--base-accent-color);
}

.rail__item:has(> .rail__box--active)::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  width: 3px;
  height: 24px;
  border-radius: 0 3px 3px 0;
  background: var(--base-accent-color);
  transform: translateY(-50%);
}

.rail__item:focus-visible {
  outline: none;
}

.rail__item:focus-visible .rail__box {
  outline: 2px solid var(--base-accent-color);
  outline-offset: 2px;
}

.rail__footer {
  display: flex;
  justify-content: center;
  flex-shrink: 0;
  padding: 14px;
  /* PWA standalone: home-indicator не должен перекрывать UserMenu. */
  padding-bottom: max(14px, env(safe-area-inset-bottom));
}
</style>
